import { createFileRoute } from '@tanstack/react-router';
import { Schema } from 'effect';
import {
  DEFAULT_PUBLIC_DOC_CATEGORY,
  PublicDocListPage,
  PublicDocListPending,
  getPublicDocCategoryHref,
  getPublicDocListHref,
  normalizePublicDocCategory,
  publicDocSearchSchema,
  publicDocsPageOptions,
} from '@/components/astro/features/public-pdf-doc/public-doc-list-page';
import { seo } from '@/seo/seo';
import { normalizeCanonicalUrl } from '@/seo/canonical';
import { clientEnv } from '@/env';
import { cacheHeadersFor, experimentalRoutes } from '@/analytics';

const siteUrl = clientEnv.siteUrl.replace(/\/$/, '');

function toAbsoluteUrl(path: string) {
  return normalizeCanonicalUrl(`${siteUrl}${path}`);
}

const routeAnalytics = experimentalRoutes['/_marketing/doc/'];

export const Route = createFileRoute('/_marketing/doc/')({
  validateSearch: Schema.toStandardSchemaV1(publicDocSearchSchema),
  staticData: { analytics: routeAnalytics },
  headers: cacheHeadersFor(routeAnalytics),
  loaderDeps: ({ search }) => ({
    search: search.search,
    categoryParam: search.category,
    category: normalizePublicDocCategory(search.category),
    page: search.page ?? 1,
    cursor: search.cursor,
  }),
  loader: async ({ deps, context }) => {
    const basePath =
      deps.category === DEFAULT_PUBLIC_DOC_CATEGORY && !deps.categoryParam
        ? '/doc'
        : getPublicDocCategoryHref(deps.category);

    const publicDocsPage = await context.queryClient.ensureQueryData(
      publicDocsPageOptions({
        page: deps.page,
        cursor: deps.cursor,
        search: deps.search,
        category: deps.categoryParam ? deps.category : undefined,
      }),
    );

    return {
      canonical: getPublicDocListHref(basePath, {
        page: publicDocsPage.page,
      }),
      noindex: Boolean(deps.search || deps.categoryParam),
    };
  },
  head: ({ loaderData }) => {
    const canonical = toAbsoluteUrl(loaderData?.canonical ?? '/doc');
    const description =
      'Explore, download, and access PDFs, articles, and research documents grouped by category.';

    return {
      meta: [
        ...seo({
          title: 'Document Repository | Listening',
          description,
        }),
        {
          name: 'robots',
          content: loaderData?.noindex ? 'noindex, follow' : 'index, follow',
        },
      ],
      links: [{ rel: 'canonical', href: canonical }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Document Repository',
            description,
            url: canonical,
          }),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: toAbsoluteUrl('/'),
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Documents',
                item: toAbsoluteUrl('/doc'),
              },
            ],
          }),
        },
      ],
    };
  },
  pendingMs: 0,
  pendingComponent: () => (
    <PublicDocListPending selectedCategory={DEFAULT_PUBLIC_DOC_CATEGORY} />
  ),
  component: RouteComponent,
});

function RouteComponent() {
  const { search, category, cursor } = Route.useSearch();
  const normalizedCategory = normalizePublicDocCategory(category);
  const selectedCategory = category ? normalizedCategory : undefined;
  const page = Route.useSearch({ select: (s) => s.page ?? 1 });
  const basePath = category
    ? getPublicDocCategoryHref(normalizedCategory)
    : '/doc';

  return (
    <PublicDocListPage
      selectedCategory={selectedCategory}
      search={search}
      categoryFilter={category ? normalizedCategory : undefined}
      page={page}
      cursor={cursor}
      basePath={basePath}
      heading='Document Repository'
      description='Explore, download, and access PDFs, articles, and research documents grouped by category.'
    />
  );
}
